import { type DocumentNode, useApolloClient } from "@apollo/client";
import { useSnackbar } from "notistack";
import React from "react";
import {
  filterRedundantChildren,
  sortItemIdsByTreeOrder,
} from "../doc-items/tree-helpers.js";
import type { DocDataGrid_ItemFragment } from "./DocDataGrid.generated.js";
import type { DocDataGridRow, PendingCellEdit } from "./types.js";

interface Options<T> {
  allDocItems: T[];
  batchMove: (itemIds: string[], targetItemId: string) => Promise<unknown>;
  clearChecked: () => void;
  fragment: DocumentNode;
  fragmentName: string;
}

/**
 * Shared row-mutation wiring for the document DataGrid mount components
 * (project quote + template). Owns the per-row "saving" set, the bulk-move
 * handler, and the inline-edit `processRowUpdate` that applies a pending cell
 * edit and re-reads the row from the Apollo cache. The only per-document
 * differences are the GraphQL fragment used to read the updated row back.
 */
export function useDocDataGridRowMutations<
  T extends DocDataGrid_ItemFragment & { originVersionNumber?: number | null },
>({
  allDocItems,
  batchMove,
  clearChecked,
  fragment,
  fragmentName,
}: Options<T>): {
  isSavingRow: (rowId: string) => boolean;
  handleMove: (targetItemId: string, itemIds: string[]) => Promise<void>;
  processRowUpdate: (
    newRow: DocDataGridRow<T> & { _pendingEdit?: PendingCellEdit<Partial<T>> }
  ) => Promise<DocDataGridRow<T>>;
} {
  const client = useApolloClient();
  const { enqueueSnackbar } = useSnackbar();

  const [savingRowIds, setSavingRowIds] = React.useState<Set<string>>(
    () => new Set()
  );
  const isSavingRow = React.useCallback(
    (rowId: string) => savingRowIds.has(rowId),
    [savingRowIds]
  );

  const addSavingRows = React.useCallback((ids: string[]) => {
    setSavingRowIds(prev => {
      const next = new Set(prev);
      for (const id of ids) next.add(id);
      return next;
    });
  }, []);
  const removeSavingRows = React.useCallback((ids: string[]) => {
    setSavingRowIds(prev => {
      const next = new Set(prev);
      for (const id of ids) next.delete(id);
      return next;
    });
  }, []);

  const handleMove = React.useCallback(
    async (targetItemId: string, itemIds: string[]) => {
      const filteredIds = filterRedundantChildren(itemIds, allDocItems);
      const sortedIds = sortItemIdsByTreeOrder(filteredIds, allDocItems);
      const allSavingIds = [...itemIds, targetItemId];
      addSavingRows(allSavingIds);
      try {
        await batchMove(sortedIds, targetItemId);
        clearChecked();
      } finally {
        removeSavingRows(allSavingIds);
      }
    },
    [allDocItems, batchMove, clearChecked, addSavingRows, removeSavingRows]
  );

  const processRowUpdate = React.useCallback(
    async (
      newRow: DocDataGridRow<T> & {
        _pendingEdit?: PendingCellEdit<Partial<T>>;
      }
    ) => {
      if (!newRow._pendingEdit || !newRow.item) return newRow;
      addSavingRows([newRow.id]);
      try {
        await newRow._pendingEdit.apply(newRow.item.id);
        const updatedItem = client.readFragment<T>({
          id: client.cache.identify({
            __typename: "Item",
            id: newRow.item.id,
            originVersionNumber: newRow.item.originVersionNumber,
          }),
          fragment,
          fragmentName,
        });
        return { ...newRow, item: { ...newRow.item, ...updatedItem } };
      } catch (e) {
        if (e instanceof Error)
          enqueueSnackbar(e.message, { variant: "error" });
        throw e;
      } finally {
        removeSavingRows([newRow.id]);
      }
    },
    [
      client,
      enqueueSnackbar,
      fragment,
      fragmentName,
      addSavingRows,
      removeSavingRows,
    ]
  );

  return { isSavingRow, handleMove, processRowUpdate };
}
